import { useContext, useMemo } from 'react';
import { omit } from 'lodash-es';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { AdminFormAction, AdminPageHeader, Form } from '@ui';
import { EmployeeForm, useEmployeeFormSchema, getDefaultValues } from '@lp/features/admin/forms/EmployeeForm';
import { useSetHomeLocation } from '@lp/core/hooks/useSetHomeLocation';
import { AuthContext } from '@lp/core/auth';
import { EmployeeFormType, EmployeeFormValues } from '@/types';
import { Employee, EmployeeRole, EmployeeType } from '@/api/apis/resources';
import {
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useUpdateEmployeesMutation,
  useUpdateCLMutation,
  useUpdateCSMutation,
  useActivateCSMutation,
  useActivateCLMutation,
  useDeactivateCSMutation,
  useDeactivateCLMutation,
} from '@/queries/employee/employee.mutation';
import { useLoaderData } from '@/hooks/useLoaderData';
import { getChangedValues, remapKeys } from '@/utils/helpers';
import { EmployeeLocation } from '@/api/types';
import { AirportType, Hub } from '@/api/apis/facilities';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';

type EmployeesEditProps = {
  type: EmployeeType;
};

type EmployeeLoader = {
  employee: Employee;
  homeLocation?: EmployeeLocation;
  airport?: AirportType;
  baseLocation?: Hub;
};

export const EmployeesEdit = ({ type }: EmployeesEditProps) => {
  const navigate = useNavigate();
  const { profile, checkPermission } = useContext(AuthContext);
  const { employee, homeLocation, baseLocation } = useLoaderData<EmployeeLoader>();
  const { mutateAsync: updateEmployeesMutation } = useUpdateEmployeesMutation(profile?.userId);
  const { mutateAsync: updateCLMutation } = useUpdateCLMutation(profile?.userId);
  const { mutateAsync: updateCSMutation } = useUpdateCSMutation(profile?.userId);
  const setHomeLocation = useSetHomeLocation(homeLocation?.id);
  const { mutateAsync: activateEmployeeAsync } = useActivateEmployeeMutation();
  const { mutateAsync: activateCSAsync } = useActivateCSMutation();
  const { mutateAsync: activateCLAsync } = useActivateCLMutation();
  const { mutateAsync: deactivateEmployeeAsync } = useDeactivateEmployeeMutation();
  const { mutateAsync: deactivateCSAsync } = useDeactivateCSMutation();
  const { mutateAsync: deactivateCLAsync } = useDeactivateCLMutation();
  const { uploadFiles } = useUploadFile(FileType.Resource);

  const hasEmployeeEditPermission = checkPermission(AdminPermissions.TmdxUsersUpdate);
  const hasCSEditPermission = checkPermission(AdminPermissions.TmdxUsersCSUpdate);
  const hasCSTrialProgramEditPermission = checkPermission(AdminPermissions.TmdxUsersCSUpdateTrialProgram);
  const hasCLEditPermission = checkPermission(AdminPermissions.TmdxUsersCLUpdate);

  const canCSEdit = hasCSEditPermission && employee.roles.includes(EmployeeRole.ClinicalSpecialist);
  const canCLEdit = hasCLEditPermission && employee.roles.includes(EmployeeRole.ClinicalLead);
  const canCSSubmit = canCSEdit || hasCSTrialProgramEditPermission;

  const canEditEmployee = hasEmployeeEditPermission || canCSEdit || canCLEdit;
  const canSubmitEmployee = canEditEmployee || canCSSubmit;
  const { schema, isLoading: isGeoDataLoading } = useEmployeeFormSchema();

  const employeeMutations = useMemo(() => {
    if (canCSSubmit) {
      return {
        update: updateCSMutation,
        activate: activateCSAsync,
        deactivate: deactivateCSAsync,
      };
    }

    if (canCLEdit) {
      return {
        update: updateCLMutation,
        activate: activateCLAsync,
        deactivate: deactivateCLAsync,
      };
    }

    return {
      update: updateEmployeesMutation,
      activate: activateEmployeeAsync,
      deactivate: deactivateEmployeeAsync,
    };
  }, [
    canCLEdit,
    canCSSubmit,
    activateCLAsync,
    activateCSAsync,
    activateEmployeeAsync,
    deactivateCLAsync,
    deactivateCSAsync,
    deactivateEmployeeAsync,
    updateCLMutation,
    updateCSMutation,
    updateEmployeesMutation,
  ]);

  const isContractor = type === EmployeeType.contractor;

  const defaultValues = useMemo<EmployeeFormType>(
    () => getDefaultValues(employee, homeLocation, baseLocation),
    [employee, homeLocation, baseLocation],
  );

  const handleBackToList = () => {
    navigate(`/admin/users/${type}s`);
  };

  const handleSubmit = async (values: EmployeeFormType) => {
    const { country, addressLine, state, city, postalCode, latitude, longitude, homeLocationTimezone } = values;
    const locationValues = { country, addressLine, state, city, postalCode, latitude, longitude };

    try {
      let uploadedFile;

      if (values.image) {
        uploadedFile = await uploadFiles([values.image]);
      }

      const updateBody = {
        profilePhoto: uploadedFile ? uploadedFile[0].fileName : values.profilePhoto,
        roles: values.roles,
        organSpecializations: values.organType,
        phone: values.phone,
        hireDate: values.hireDate,
        tsaPrecheckNumber: values.tsaPrecheckNumber,
        isActive: values.isActive,
        scrubSizeTop: values.scrubSizeTop,
        scrubSizeBottom: values.scrubSizeBottom,
        weight: values.weight,
        baseLocationId: values.baseLocationId,
        closestAirportForDeployment: values.closestAirportForDeploymentId
          ? Number(values.closestAirportForDeploymentId)
          : null,
        locationPreference: values.locationPreference,
        canWorkOnExtraShifts: values.canWorkOnExtraShifts,
        extraShiftsCount: values.extraShiftsCount,
        canTravel: values.canTravel,
        travelShiftsCount: values.travelShiftsCount,
        clinicalSpecialistShiftGroupType: values.clinicalSpecialistShiftGroupType
          ? values.clinicalSpecialistShiftGroupType
          : null,
        shiftTimeGroup: values.shiftTimeGroup ? values.shiftTimeGroup : null,
        liverSurgeonRegionId: values.liverSurgeonRegionId ? values.liverSurgeonRegionId : null,
        heartLungsSurgeonRegionId: values.heartLungsSurgeonRegionId ? values.heartLungsSurgeonRegionId : null,
        clinicalCoordinatorType: values.clinicalCoordinatorType ? values.clinicalCoordinatorType : null,
        phoneSupportOrganSpecializations: values.phoneSupportOrganSpecializations,
        rsScheduleManagerOrgans: values.rsScheduleManagerOrgans,
        organManagementSpecialistSpecializations: values.organManagementSpecialistSpecializations,
        shadowOrganSpecializations: values.shadowOrganSpecializations,
        proctorOrganSpecializations: values.proctorOrganSpecializations,
        trialProgramIds: values.trialProgramIds,
      };

      const updatedLocation = {
        ...locationValues,
        timezone: homeLocationTimezone || '',
      };

      let homeLocationId = homeLocation?.id;
      let isLocationChanged = false;
      if (homeLocation) {
        const locationChangedValues = getChangedValues(updatedLocation, homeLocation);
        isLocationChanged = Object.keys(locationChangedValues).length > 0;
      }

      if (isLocationChanged || !homeLocation) {
        homeLocationId = await setHomeLocation(updatedLocation);
      }

      const changedValues = getChangedValues<Partial<EmployeeFormValues>>(updateBody, defaultValues);

      const updateMutationBody = {
        id: employee.id,
        ...remapKeys(omit(changedValues, 'isActive'), { baseLocationId: 'baseLocation' }),
        homeLocation: homeLocationId,
        phoneSupportOrganSpecializations: updateBody.phoneSupportOrganSpecializations,
        rsScheduleManagerOrgans: updateBody.rsScheduleManagerOrgans,
        organManagementSpecialistSpecializations: updateBody.organManagementSpecialistSpecializations,
        shadowOrganSpecializations: updateBody.shadowOrganSpecializations,
        proctorOrganSpecializations: updateBody.proctorOrganSpecializations,
      };

      await employeeMutations.update(updateMutationBody);

      if ('isActive' in changedValues) {
        const mutationFn = changedValues.isActive ? employeeMutations.activate : employeeMutations.deactivate;
        await mutationFn(employee.id);
      }
      toast.success(`${type.toUpperCase()} updated successfully.`);
      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const title = isContractor ? 'Edit Contractor' : 'Edit Employee';

  if (isGeoDataLoading) return null;

  return (
    <Form<EmployeeFormType> defaultValues={defaultValues} schema={schema} onSubmit={handleSubmit}>
      <div className="flex flex-col gap-9">
        <AdminPageHeader title={title} backToListLink={`/admin/users/${type}s`}>
          <AdminFormAction type="save" handleClick={handleBackToList} hasEditPermissions={canSubmitEmployee} />
        </AdminPageHeader>
        <EmployeeForm
          trackedLocation={employee.trackedLocation}
          isContractor={isContractor}
          isReadOnly={!canEditEmployee}
          baseLocation={baseLocation}
        />
      </div>
    </Form>
  );
};
